import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { BadResponseWithError } from '@shared/models/common.interface';
import { NotificationsService } from '@shared/services/notifications.service';
import { catchError, map, of, switchMap } from 'rxjs';
import { RewardsActions } from '../actions/actions-type.action';
import { ChallengesService } from '../../services/rewards.service';

@Injectable({ providedIn: 'root' })
export class RewardsEffect {
    fetchChallengesRewards$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RewardsActions.fetchChallengesRequests),
            switchMap(({ categoryId }) =>
                this.challengesService.fetchChallenges(categoryId).pipe(
                    map((response) => {
                        return RewardsActions.fetchChallengesSuccess({
                            payload: response,
                        });
                    }),
                    catchError((response: BadResponseWithError) => {
                        this.notificationService.showError(
                            response.error.message
                        );
                        return of(
                            RewardsActions.fetchChallengesFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );
    fetchCategoriesForRewards$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RewardsActions.fetchCategoriesForRewardsRequests),
            switchMap(() =>
                this.challengesService.fetchCategoriesForChallenges().pipe(
                    map((response) => {
                        return RewardsActions.fetchCategoriesForRewardsSuccess({
                            payload: response,
                        });
                    }),
                    catchError((response: BadResponseWithError) => {
                        this.notificationService.showError(
                            response.error.message
                        );
                        return of(
                            RewardsActions.fetchCategoriesForRewardsFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );
    fetchEventTypesForRewards$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RewardsActions.fetchEventTypesForRewardsRequests),
            switchMap(() =>
                this.challengesService.fetchEventsForChallenges().pipe(
                    map((response) => {
                        return RewardsActions.fetchEventTypesForRewardsSuccess({
                            payload: response,
                        });
                    }),
                    catchError((response: BadResponseWithError) => {
                        this.notificationService.showError(
                            response.error.message
                        );
                        return of(
                            RewardsActions.fetchEventTypesForRewardsFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );
    createSingleRewards$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RewardsActions.createSingleRewardsRequests),
            switchMap(({ payload }) =>
                this.challengesService.createSingleReward(payload).pipe(
                    map((response) => {
                        this.notificationService.showSuccessMessage(
                            'Challenge created successfully'
                        );
                        return RewardsActions.createSingleRewardsSuccess({
                            payload: response,
                        });
                    }),
                    catchError((response: BadResponseWithError) => {
                        this.notificationService.showError(
                            response.error.message
                        );
                        return of(
                            RewardsActions.createSingleRewardsFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );
    createGroupRewards$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RewardsActions.createGroupRewardsRequests),
            switchMap(({ payload }) =>
                this.challengesService.createGroupReward(payload).pipe(
                    map((response) => {
                        this.notificationService.showSuccessMessage(
                            'Group created successfully'
                        );
                        return RewardsActions.createGroupRewardsSuccess({
                            payload: response,
                        });
                    }),
                    catchError((response: BadResponseWithError) => {
                        this.notificationService.showError(
                            response.error.message
                        );
                        return of(
                            RewardsActions.createGroupRewardsFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );
    updateSingleRewards$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RewardsActions.updateSingleRewardsRequests),
            switchMap(({ payload }) =>
                this.challengesService
                    .updateSingleReward(payload.challengeId, payload.body)
                    .pipe(
                        map((response) => {
                            this.notificationService.showSuccessMessage(
                                'Challenge updated successfully'
                            );
                            return RewardsActions.updateSingleRewardsSuccess({
                                payload: payload,
                            });
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationService.showError(
                                response.error.message
                            );
                            return of(
                                RewardsActions.updateSingleRewardsFailure({
                                    payload: response,
                                })
                            );
                        })
                    )
            )
        )
    );
    updateGroupRewards$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RewardsActions.updateGroupRewardsRequests),
            switchMap(({ payload }) =>
                this.challengesService
                    .updateGroupReward(payload.groupId, payload.body)
                    .pipe(
                        map((response) => {
                            this.notificationService.showSuccessMessage(
                                'Group updated successfully'
                            );
                            return RewardsActions.updateGroupRewardsSuccess({
                                payload: payload,
                            });
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationService.showError(
                                response.error.message
                            );
                            return of(
                                RewardsActions.updateGroupRewardsFailure({
                                    payload: response,
                                })
                            );
                        })
                    )
            )
        )
    );
    createChallengesForGroupRewards$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RewardsActions.createChallengeForGroupRewardsRequests),
            switchMap(({ payload }) =>
                this.challengesService
                    .createChallengeInGroupReward(payload.groupId, payload.body)
                    .pipe(
                        map((response) => {
                            this.notificationService.showSuccessMessage(
                                'Group Challenge created successfully'
                            );
                            return RewardsActions.createChallengeForGroupRewardsSuccess(
                                {
                                    payload: {
                                        groupId: payload.groupId,
                                        challenge: response,
                                    },
                                }
                            );
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationService.showError(
                                response.error.message
                            );
                            return of(
                                RewardsActions.createChallengeForGroupRewardsFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    )
            )
        )
    );
    updateChallengesForGroupRewards$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RewardsActions.updateChallengeForGroupRewardsRequests),
            switchMap(({ payload }) =>
                this.challengesService
                    .updateChallengeInGroupReward(
                        payload.groupId,
                        payload.challengeId,
                        payload.body
                    )
                    .pipe(
                        map((response) => {
                            this.notificationService.showSuccessMessage(
                                'Group Challenge updated successfully'
                            );
                            return RewardsActions.updateChallengeForGroupRewardsSuccess(
                                {
                                    payload: {
                                        groupId: payload.groupId,
                                        challengeId: payload.challengeId,
                                        body: response,
                                    },
                                }
                            );
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationService.showError(
                                response.error.message
                            );
                            return of(
                                RewardsActions.updateChallengeForGroupRewardsFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    )
            )
        )
    );

    deleteChallenge$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RewardsActions.deleteChallengeRequests),
            switchMap(({ payload }) =>
                this.challengesService
                    .deleteChallenge(payload.challengeId)
                    .pipe(
                        map((response) => {
                            this.notificationService.showSuccessMessage(
                                'Challenge deleted successfully'
                            );
                            return RewardsActions.deleteChallengeSuccess({
                                payload: payload,
                            });
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationService.showError(
                                response.error.message
                            );
                            return of(
                                RewardsActions.deleteChallengeFailure({
                                    payload: response,
                                })
                            );
                        })
                    )
            )
        )
    );
    deleteGroupChallenge$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RewardsActions.deleteGroupChallengeRequests),
            switchMap(({ payload }) =>
                this.challengesService
                    .deleteGroupChallenge(payload.groupId)
                    .pipe(
                        map((response) => {
                            this.notificationService.showSuccessMessage(
                                'Group deleted successfully'
                            );
                            return RewardsActions.deleteGroupChallengeSuccess({
                                payload: payload,
                            });
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationService.showError(
                                response.error.message
                            );
                            return of(
                                RewardsActions.deleteGroupChallengeFailure({
                                    payload: response,
                                })
                            );
                        })
                    )
            )
        )
    );

    changeDisplayOrder$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RewardsActions.changeDisplayOrderRequests),
            switchMap(({ payload }) =>
                this.challengesService
                    .updateChallengesDisplayOrder(payload.displayNumbers)
                    .pipe(
                        map(() => {
                            this.notificationService.showSuccessMessage(
                                'Display order updated'
                            );
                            return RewardsActions.changeDisplayOrderSuccess({
                                payload,
                            });
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationService.showError(
                                response.error.message
                            );
                            return of(
                                RewardsActions.changeDisplayOrderFailure({
                                    payload: response,
                                })
                            );
                        })
                    )
            )
        )
    );

    fetchEvergreens$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RewardsActions.fetchEvergreensRequest),
            switchMap(() =>
                this.challengesService.fetchEvergreens().pipe(
                    map((response) => {
                        return RewardsActions.fetchEvergreensSuccess({
                            payload: response,
                        });
                    }),
                    catchError((response: BadResponseWithError) => {
                        this.notificationService.showError(
                            response.error.message
                        );
                        return of(
                            RewardsActions.fetchEvergreensFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );

    createEvergreens$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RewardsActions.createEvergreensRequest),
            switchMap(({ payload }) =>
                this.challengesService.createEvergreens(payload).pipe(
                    map((response) => {
                        this.notificationService.showSuccessMessage(
                            'Challenge created successfully'
                        );
                        return RewardsActions.createEvergreensSuccess({
                            payload: response,
                        });
                    }),
                    catchError((response: BadResponseWithError) => {
                        this.notificationService.showError(
                            response.error.message
                        );
                        return of(
                            RewardsActions.createEvergreensFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );

    updateEvergreens$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RewardsActions.updateEvergreensRequest),
            switchMap(({ payload }) =>
                this.challengesService
                    .updateEvergreens(payload.challengeId, payload.body)
                    .pipe(
                        map((response) => {
                            this.notificationService.showSuccessMessage(
                                'Challenge updated successfully'
                            );
                            return RewardsActions.updateEvergreensSuccess({
                                payload: payload,
                            });
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationService.showError(
                                response.error.message
                            );
                            return of(
                                RewardsActions.updateEvergreensFailure({
                                    payload: response,
                                })
                            );
                        })
                    )
            )
        )
    );

    fetchPerks$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RewardsActions.fetchPerksRequest),
            switchMap(() =>
                this.challengesService.fetchPerks().pipe(
                    map((response) => {
                        return RewardsActions.fetchPerksSuccess({
                            payload: response,
                        });
                    }),
                    catchError((response: BadResponseWithError) => {
                        this.notificationService.showError(
                            response.error.message
                        );
                        return of(
                            RewardsActions.fetchPerksFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private challengesService: ChallengesService,
        private notificationService: NotificationsService
    ) {}
}
